import './index.css';

import React from 'react';
import { RouterProvider } from 'react-router';
import { ThemeProvider } from '@mui/material/styles';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { ErrorBoundary, reactPlugin } from '@datadog/browser-rum-react';
import { createBrowserRouter } from '@datadog/browser-rum-react/react-router-v7';
import { SplitFactoryProvider } from '@splitsoftware/splitio-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { createRoot } from 'react-dom/client';
import { Userpilot } from 'userpilot';

import packageJson from '../package.json';
import App from './App';
import SiteWrapper from './appWrapper/SiteWrapper';
import getEnv from './env';
import ErrorPage from './features/Home/ErrorPage';
import { allRoutes } from './routes/Router';
import { materialTheme } from './theme/theme';
import { splitConfig } from './utils/split/splitConfig';

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Failed to find the root element');
const { version } = packageJson;
rootElement.setAttribute('version', version.substring(1));
const root = createRoot(rootElement);
const queryClient = new QueryClient();

datadogLogs.init({
  version: packageJson.version,
  clientToken: getEnv().react_app_dd_client_id,
  service: 'landlord-portal-frontend',
  env: getEnv().app_env,
  site: 'datadoghq.com',
  forwardErrorsToLogs: true,
  forwardConsoleLogs: ['info', 'warn', 'error'],
  sessionSampleRate: 100,
});

datadogRum.init({
  version,
  site: 'datadoghq.com',
  env: getEnv().app_env,
  applicationId: getEnv().react_app_dd_app_id,
  clientToken: getEnv().react_app_dd_client_id,
  service: 'landlord-portal-frontend',
  sessionSampleRate: 100,
  sessionReplaySampleRate: 0,
  trackUserInteractions: true,
  trackResources: true,
  trackLongTasks: true,
  defaultPrivacyLevel: 'mask-user-input',
  plugins: [reactPlugin({ router: true })],
  allowedTracingUrls: [
    /propertymanager\.((development|integration|staging)\.)?theguarantors\.com/,
    /deals-dashboard\.((development|integration|staging)\.)?theguarantors\.com/,
    /claims-portal\.((development|integration|staging)\.)?theguarantors\.com/,
    /landlord-portal-backend\.((development|integration|staging)\.)?theguarantors\.com/,
  ],
});

//look for production url for userpilot
if (window.location.hostname === 'propertymanager.theguarantors.com') {
  Userpilot.initialize(getEnv().userpilot_api_key.replace('STG-', ''));
} else {
  Userpilot.initialize(getEnv().userpilot_api_key);
}

const router = createBrowserRouter([
  {
    element: <App />,
    children: [
      {
        element: <SiteWrapper />,
        children: allRoutes,
      },
    ],
  },
]);

root.render(
  <React.StrictMode>
    <ErrorBoundary fallback={ErrorPage}>
      <SplitFactoryProvider config={splitConfig}>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider theme={materialTheme}>
            <RouterProvider router={router} />
          </ThemeProvider>
          <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
        </QueryClientProvider>
      </SplitFactoryProvider>
    </ErrorBoundary>
  </React.StrictMode>,
);
